import React, { useCallback, useContext, useState, useRef, useEffect } from "react";
import { v4 as uuid4 } from "uuid";
import SessionContext, { MessageProps } from "../../context/session";
import { ChatInputWrapper, ChatInputContainer, ChatMessage, SendMessage } from "./style";
import "./ChatInputImage.css";
import "./DeepThinkingToggle.css";
import { apiUrl, wsUrl } from "../../config/runtime";
import {
  isRunFailedEvent,
  isPersistenceFailedEvent,
  normalizeChatMessage,
} from "../../types/contracts";

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const SparklesIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l2.4 7.2L21.6 11.6l-7.2 2.4L12 21.2l-2.4-7.2L2.4 11.6l7.2-2.4z" />
  </svg>
);

const SendIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const StopIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <rect x="5" y="5" width="14" height="14" rx="3" />
  </svg>
);

const LoaderSpinner = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    style={{ animation: "cl-spin 0.8s linear infinite" }}
  >
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
);

const GEAR_VALUES = [1024, 2048, 4096, 8192];
const GEAR_LABELS = ["1K 极速", "2K 平衡", "4K 深度", "8K 极限"];
const GEAR_NAMES = ["1K 极速", "2K 平衡", "4K 深度", "8K 极限"];

const getIndexFromBudget = (budget: number) => {
  const idx = GEAR_VALUES.indexOf(budget);
  return idx !== -1 ? idx : 1;
};

const createUserMessage = (content: string): MessageProps => ({
  protocol_version: 1,
  id: uuid4(),
  request_id: null,
  run_id: null,
  role: "user",
  content,
  artifacts: [],
  status: "completed",
  created_at: new Date().toISOString(),
});

const replayCursorStorageKey = (token: string) => `ws_replay_cursor_${token}`;

const readReplayCursor = (token: string) => {
  try {
    return window.localStorage.getItem(replayCursorStorageKey(token)) || "";
  } catch {
    return "";
  }
};

const saveReplayCursor = (token: string, replayId: string) => {
  try {
    window.localStorage.setItem(replayCursorStorageKey(token), replayId);
  } catch {
    // 浏览器禁用存储时退化为无 cursor 重连，不影响当前实时渲染。
  }
};

interface ChatInputProps {
  chat: any;
  setChat: any;
  onControlError: React.Dispatch<React.SetStateAction<string>>;
}

const ChatInput: React.FC<ChatInputProps> = ({ onControlError }) => {
  const [chatInput, setChatInput] = useState("");
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isComposing, setIsComposing] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<"idle" | "generating" | "stopping">("idle");

  const [deepThinking, setDeepThinking] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem("deep_thinking_enabled");
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  const [thinkingBudget, setThinkingBudget] = useState<number>(() => {
    try {
      const saved = localStorage.getItem("thinking_budget");
      return saved !== null ? Number(saved) : 2048;
    } catch {
      return 2048;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("deep_thinking_enabled", JSON.stringify(deepThinking));
    } catch {
      /* ignore */
    }
  }, [deepThinking]);

  useEffect(() => {
    try {
      localStorage.setItem("thinking_budget", String(thinkingBudget));
    } catch {
      /* ignore */
    }
  }, [thinkingBudget]);

  const [dragIndex, setDragIndex] = useState<number>(1);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [showThinkingPopover, setShowThinkingPopover] = useState(false);

  const [showImagePopover, setShowImagePopover] = useState(false);
  const [tempUrl, setTempUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { setMessages, token, setSocketState, draftInput, setDraftInput, setToolStatus } = useContext(SessionContext);

  useEffect(() => {
    if (draftInput) {
      setChatInput(draftInput);
      setDraftInput("");
      textareaRef.current?.focus();
    }
  }, [draftInput, setDraftInput]);

  useEffect(() => {
    setDragIndex(getIndexFromBudget(thinkingBudget));
  }, [thinkingBudget]);

  /* 📍 自动适应文本框高度 (1 行 ~ 5 行) */
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(160, textareaRef.current.scrollHeight)}px`;
    }
  }, [chatInput]);

  /* 📍 点击外部区域自动关闭 Popover */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowThinkingPopover(false);
      }
    };
    if (showThinkingPopover) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showThinkingPopover]);

  const handleChange = (event: any) => {
    setChatInput(event.target.value);
  };

  const ws = useRef<any>();
  // 当前 WebSocket 连接中正在执行的唯一一轮 Agent 运行。
  const activeRunIdRef = useRef<string | null>(null);
  // 网关尚未返回 run_accepted 时，先记录停止意图，收到 run_id 后立即补发。
  const pendingStopRef = useRef(false);

  const clearActiveRun = useCallback(() => {
    activeRunIdRef.current = null;
    pendingStopRef.current = false;
  }, []);

  const sendWebSocketJson = useCallback((payload: unknown) => {
    const socket = ws.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(payload));
    return true;
  }, []);

  useEffect(() => {
    if (!token) return;

    let disposed = false;
    let reconnectAttempt = 0;
    let reconnectTimer: number | null = null;
    let heartbeatTimer: number | null = null;
    let wsCurrent: WebSocket | null = null;

    let streamTimer: any = null;

    const handleMessage = (event: any) => {
      const message = JSON.parse(event.data);

      if (typeof message.replay_id === "string" && message.replay_id) {
        saveReplayCursor(token, message.replay_id);
      }

      if (message.type === "run_accepted" && typeof message.run_id === "string") {
        activeRunIdRef.current = message.run_id;

        // 用户可能在 XADD 成功、run_accepted 返回前就点击了停止。
        if (pendingStopRef.current) {
          if (sendWebSocketJson({ type: "stop", run_id: message.run_id })) {
            pendingStopRef.current = false;
          }
        }
        return;
      }

      if (message.type === "stop_ack") {
        // stop_ack 只代表 Redis 已记录取消请求，不能证明 Worker 已经停止。
        return;
      }

      if (message.type === "tool.started") {
        setToolStatus({ title: message.title, tool_name: message.tool_name });
        return;
      }

      if (message.type === "tool.completed") {
        setToolStatus(null);
        return;
      }

      if (message.type === "pong") return;

      if (isPersistenceFailedEvent(message)) {
        console.error(
          "Chat history persistence failed:",
          message.request_id,
          message.run_id
        );
        clearActiveRun();
        setGenerationStatus("idle");
        setToolStatus(null);
        onControlError(message.detail);
        return;
      }

      if (isRunFailedEvent(message)) {
        console.error(
          "Agent run failed:",
          message.payload.code,
          message.run_id
        );
        clearActiveRun();
        setGenerationStatus("idle");
        setToolStatus(null);
        onControlError(message.payload.detail);
        return;
      }

      if (message.type === "error") {
        console.error("WebSocket protocol error:", message.detail || message.code);
        clearActiveRun();
        setGenerationStatus("idle");
        setToolStatus(null);
        return;
      }

      if (message.type === "stream_end") {
        clearActiveRun();
        setGenerationStatus("idle");
        setToolStatus(null);
        return;
      }

      const chatMessage = normalizeChatMessage(message);
      if (!chatMessage) return;

      if (chatMessage.content.includes("[用户已中途停止回答]")) {
        clearActiveRun();
        setGenerationStatus("idle");
        setToolStatus(null);
      }

      setMessages((prevMessages: MessageProps[]) => {
        const index = prevMessages.findIndex((m) => m.id === chatMessage.id);
        if (index !== -1) {
          const updated = [...prevMessages];
          updated[index] = chatMessage;
          return updated;
        }
        return prevMessages.concat(chatMessage);
      });
    };

    const connectWebSocket = () => {
      if (disposed) return;

      const replayCursor = readReplayCursor(token);
      const replayQuery = replayCursor
        ? `&last_replay_id=${encodeURIComponent(replayCursor)}`
        : "";
      const connection = new WebSocket(
        wsUrl(`/chat?token=${encodeURIComponent(token)}${replayQuery}`)
      );
      wsCurrent = connection;
      ws.current = connection;

      connection.onopen = () => {
        reconnectAttempt = 0;
        setSocketState("active");

        // Run ID 可能在断线前已经确认；若用户在断线期间点击停止，
        // 不会再次收到 run_accepted，因此在重连成功后直接补发停止帧。
        if (pendingStopRef.current && activeRunIdRef.current) {
          if (sendWebSocketJson({
            type: "stop",
            run_id: activeRunIdRef.current,
          })) {
            pendingStopRef.current = false;
          }
        }
      };
      connection.onclose = (event: CloseEvent) => {
        if (heartbeatTimer !== null) {
          window.clearInterval(heartbeatTimer);
          heartbeatTimer = null;
        }

        if (disposed) return;

        // 1008 表示 Token/Session 无效，继续重试没有意义。
        if (event.code === 1008 || event.code === 1000) {
          clearActiveRun();
          setGenerationStatus("idle");
          setSocketState("");
          return;
        }

        setSocketState("reconnecting");
        const delay = Math.min(30000, 1000 * 2 ** reconnectAttempt);
        reconnectAttempt += 1;
        reconnectTimer = window.setTimeout(connectWebSocket, delay);
      };
      connection.onmessage = handleMessage;

      heartbeatTimer = window.setInterval(() => {
        if (connection.readyState === WebSocket.OPEN) {
          connection.send("ping");
        }
      }, 30000);
    };

    connectWebSocket();

    return () => {
      disposed = true;
      if (streamTimer) clearTimeout(streamTimer);
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      if (heartbeatTimer !== null) window.clearInterval(heartbeatTimer);
      if (wsCurrent !== null) {
        wsCurrent.close();
      }
    };
  }, [
    token,
    setMessages,
    setSocketState,
    setToolStatus,
    onControlError,
    clearActiveRun,
    sendWebSocketJson,
  ]);

  /* 📍 监听 ClarificationCard 交互卡片一键点击选择事件 */
  useEffect(() => {
    const handleSelectChoice = (e: any) => {
      let option = e.detail;
      if (option && ws.current) {
        // 防御性解析：如果 option 传进来的是 JSON 字符串，提取里面的纯文本 msg
        try {
          const parsed = typeof option === "string" ? JSON.parse(option) : option;
          if (parsed && typeof parsed === "object") {
            option = parsed.msg || parsed.option || parsed.text || option;
          }
        } catch {
          /* ignore */
        }

        onControlError("");
        setGenerationStatus("generating");
        clearActiveRun();
        const chat = createUserMessage(option);
        setMessages((prevMessages: MessageProps[]) => prevMessages.concat(chat));
        // 兼容网关 prompt 字段，并显式携带 resume_value（后端透传后可直接恢复）
        ws.current.send(
          JSON.stringify({
            prompt: option,
            message_id: chat.id,
            resume_value: option,
          })
        );
      }
    };

    window.addEventListener("send_clarification_choice", handleSelectChoice);
    return () => {
      window.removeEventListener("send_clarification_choice", handleSelectChoice);
    };
  }, [token, setMessages, onControlError, clearActiveRun]);

  const processImageFile = async (file: File) => {
    if (!file.type.startsWith("image/")) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(
        apiUrl(`/upload_image?token=${encodeURIComponent(token)}`),
        {
          method: "POST",
          body: formData,
        }
      );

      if (!res.ok) {
        throw new Error("图片上传失败");
      }

      const data = await res.json();
      setImageUrls((prev) => [...prev, data.url]);
      setPreviewUrls((prev) => [...prev, data.http_url]);
    } catch (err) {
      console.error("图片上传失败:", err);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          const val = e.target.result as string;
          setImageUrls((prev) => [...prev, val]);
          setPreviewUrls((prev) => [...prev, val]);
        }
      };
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => processImageFile(f));
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            processImageFile(file);
            e.preventDefault();
          }
        }
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    files.forEach((f) => {
      if (f.type.startsWith("image/")) {
        processImageFile(f);
      }
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleApplyImage = () => {
    if (tempUrl.trim()) {
      setImageUrls((prev) => [...prev, tempUrl.trim()]);
      setPreviewUrls((prev) => [...prev, tempUrl.trim()]);
      setTempUrl("");
    }
    setShowImagePopover(false);
  };

  const handleStopGeneration = (e?: any) => {
    if (e && e.preventDefault) e.preventDefault();
    if (ws.current && generationStatus === "generating") {
      setGenerationStatus("stopping");
      const runId = activeRunIdRef.current;
      if (runId) {
        if (!sendWebSocketJson({ type: "stop", run_id: runId })) {
          pendingStopRef.current = true;
        }
      } else {
        // 不能发送没有 run_id 的 Session 级停止请求。
        pendingStopRef.current = true;
      }
    }
  };

  const updateMessages = async (event?: any) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    // 允许：有文字，或只有图片（澄清补图场景）
    const textPart = chatInput.trim();
    const hasImages = imageUrls.length > 0;
    if ((textPart.length > 0 || hasImages) && ws.current) {
      if (ws.current.readyState !== WebSocket.OPEN) {
        onControlError("连接恢复中，请稍后再发送");
        return;
      }
      onControlError("");
      setGenerationStatus("generating");
      clearActiveRun();

      // 气泡展示用 http 预览 URL；发给后端的 prompt 用 local:// 指针（工具可直接消费）
      const displayPrefixes = previewUrls.map((u) => `[Image: ${u}]`).join("\n");
      const backendPrefixes = imageUrls.map((u) => `[Image: ${u}]`).join("\n");
      const displayText = displayPrefixes
        ? textPart
          ? `${displayPrefixes}\n${textPart}`
          : displayPrefixes
        : textPart;
      const promptText = backendPrefixes
        ? textPart
          ? `${backendPrefixes}\n${textPart}`
          : backendPrefixes
        : textPart;

      const chat = createUserMessage(displayText);

      setMessages((prevMessages: MessageProps[]) => prevMessages.concat(chat));

      const payload: any = {
        prompt: promptText,
        message_id: chat.id,
      };
      if (hasImages) {
        payload.file_urls = imageUrls;
      }
      if (deepThinking) {
        payload.deep_thinking = true;
        payload.thinking_budget = thinkingBudget;
      }

      if (!sendWebSocketJson(payload)) {
        onControlError("连接恢复中，请稍后再发送");
        return;
      }

      setChatInput("");
      setImageUrls([]);
      setPreviewUrls([]);
    }
  };

  /* 📍 按键拦截：Shift+Enter 换行，单独 Enter 发送 */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !isComposing) {
      e.preventDefault();
      updateMessages(e);
    }
  };

  const getLabelForIndex = (idx: number) => {
    const rounded = Math.round(Math.min(3, Math.max(0, idx)));
    return GEAR_LABELS[rounded];
  };

  const handlePointerDown = () => {
    setIsDragging(true);
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    const snapIndex = Math.round(Math.min(3, Math.max(0, dragIndex)));
    const targetBudget = GEAR_VALUES[snapIndex];
    setThinkingBudget(targetBudget);
    setDragIndex(snapIndex);
  };

  const activeGearIndex = getIndexFromBudget(thinkingBudget);
  const currentIndex = isDragging ? dragIndex : activeGearIndex;
  const sliderPct = Math.min(100, Math.max(0, (currentIndex / 3) * 100));

  return (
    <ChatInputWrapper onDrop={handleDrop} onDragOver={handleDragOver}>
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {(previewUrls.length > 0 || uploading) && (
        <div className="chat-input-image-preview-container" style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "8px" }}>
          {previewUrls.map((pUrl, idx) => (
            <div className="chat-input-image-preview" key={`preview-img-${idx}`}>
              <img
                src={pUrl}
                alt={`图片预览 ${idx + 1}`}
                className="chat-input-image-preview__thumb"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = "none";
                }}
              />
              <button
                type="button"
                className="chat-input-image-preview__remove"
                onClick={() => {
                  setImageUrls((prev) => prev.filter((_, i) => i !== idx));
                  setPreviewUrls((prev) => prev.filter((_, i) => i !== idx));
                }}
                title="移除图片"
              >
                ✕
              </button>
            </div>
          ))}
          {uploading && <span className="chat-input-image-preview__url" style={{ alignSelf: "center" }}>正在上传...</span>}
        </div>
      )}

      {showImagePopover && (
        <div className="chat-image-popover">
          <input
            type="text"
            className="chat-image-popover__input"
            placeholder="粘贴公开可访问的图片 URL…"
            value={tempUrl}
            onChange={(e) => setTempUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleApplyImage()}
          />
          <button
            type="button"
            className="chat-image-popover__btn"
            onClick={handleApplyImage}
          >
            确定
          </button>
        </div>
      )}

      {/* 📍 主输入框：支持 Shift+Enter 换行，Enter 发送 */}
      <ChatInputContainer onSubmit={updateMessages}>
        <ChatMessage
          ref={textareaRef}
          placeholder="给 ChatBot 发消息…"
          value={chatInput}
          rows={1}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          onPaste={handlePaste}
        />
        {generationStatus === "stopping" ? (
          <button
            type="button"
            disabled
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              border: "none",
              background: "#9ca3af",
              color: "#ffffff",
              cursor: "not-allowed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginBottom: "2px",
            }}
            title="正在中断回答..."
          >
            <LoaderSpinner />
          </button>
        ) : generationStatus === "generating" ? (
          <button
            type="button"
            aria-label="停止生成"
            onClick={handleStopGeneration}
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              border: "none",
              background: "#dc2626",
              color: "#ffffff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginBottom: "2px",
              boxShadow: "0 2px 10px rgba(220, 38, 38, 0.4)",
              transition: "transform 0.15s ease",
            }}
            title="点击停止生成"
          >
            <StopIcon />
          </button>
        ) : (
          <SendMessage type="submit" aria-label="发送消息">
            <SendIcon />
          </SendMessage>
        )}
      </ChatInputContainer>

      {/* 📍 图片与深度思考在外部 toolbar */}
      <div className="chat-input-external-toolbar">
        <div className="chat-input-external-left">
          <button
            type="button"
            className={`chat-input-add-btn ${previewUrls.length > 0 ? "chat-input-add-btn--active" : ""}`}
            onClick={() => fileInputRef.current?.click()}
            onContextMenu={(e) => {
              e.preventDefault();
              setShowImagePopover(!showImagePopover);
            }}
            title="添加图片 (点击选择本地图片，右键粘贴网络图片 URL)"
          >
            <PlusIcon />
          </button>
        </div>

        <div className="chat-input-external-right">
          <div className="deep-thinking-wrapper" ref={popoverRef}>
            <button
              type="button"
              className={`deep-thinking-btn ${
                deepThinking ? `deep-thinking-btn--gear-${activeGearIndex}` : ""
              }`}
              onClick={() => {
                const nextState = !deepThinking;
                setDeepThinking(nextState);
                setShowThinkingPopover(nextState);
              }}
              title="深度思考设置"
            >
              <SparklesIcon />
              <span>
                {deepThinking ? GEAR_NAMES[activeGearIndex] : "深度思考"}
              </span>
            </button>

            {showThinkingPopover && (
              <div className="thinking-popover-card">
                <div className="thinking-popover-header">
                  <span className="thinking-popover-title">Effort 推理深度</span>
                  <span className="thinking-popover-value">{getLabelForIndex(currentIndex)}</span>
                </div>

                <div className="thinking-popover-labels">
                  <span>Faster</span>
                  <span>Smarter</span>
                </div>

                <div className="thinking-popover-slider-box">
                  {/* 📍 4 挡位精细路标圆点 */}
                  <div className="thinking-slider-landmarks">
                    {[0, 1, 2, 3].map((gIdx) => (
                      <span
                        key={gIdx}
                        className={`thinking-landmark-dot ${
                          currentIndex >= gIdx ? "thinking-landmark-dot--active" : ""
                        }`}
                        style={{
                          left: `calc(7px + (100% - 14px) * ${gIdx / 3})`,
                        }}
                      />
                    ))}
                  </div>

                  {/* 📍 4 挡位平分等间距滑块 */}
                  <input
                    type="range"
                    min="0"
                    max="3"
                    step="0.01"
                    value={dragIndex}
                    onChange={(e) => setDragIndex(Number(e.target.value))}
                    onMouseDown={handlePointerDown}
                    onMouseUp={handlePointerUp}
                    onTouchStart={handlePointerDown}
                    onTouchEnd={handlePointerUp}
                    style={{ "--pct": `${sliderPct}%` } as React.CSSProperties}
                    className={`thinking-gradient-slider ${
                      isDragging ? "thinking-gradient-slider--dragging" : ""
                    }`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ChatInputWrapper>
  );
};

export default ChatInput;
